import { I18N } from "oc-I18N";
import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { IWarningDetailsDialog } from "oc-ui/dialogs/Dialogs";
import _ from "lodash";
import type { Arrangement } from "./Arrangement";
import { WARNUNG_WICHTIGKEIT_DEFAULT, WarnungWichtigkeit } from "./Warnung";
import { ZElement } from "./ZElement";
import { getDreifachtorPartnerElement } from "./elementFunctions";

const WARNUNG_DIALOG_HIDDEN_KEYS_STORAGE_KEY = "zaunplanerHiddenWarnungDialogKeys";

export const WARNUNG_DIALOG_KEYS = {
  KOLLISIONEN: "KOLLISIONEN",
  ECKPFOSTEN_ZWEI_TORE: "ECKPFOSTEN_ZWEI_TORE",
  TOR_ECKPFOSTEN_OEFFNUNG: "TOR_ECKPFOSTEN_OEFFNUNG",
  ZWEI_SCHIEBETORE_NEBENEINANDER: "ZWEI_SCHIEBETORE_NEBENEINANDER",
  ZWEI_EINZELTORE_NEBENEINANDER: "ZWEI_EINZELTORE_NEBENEINANDER",
  ZWEI_DOPPELTORE_NEBENEINANDER: "ZWEI_DOPPELTORE_NEBENEINANDER",
  DREI_BENACHBARTE_TORE: "DREI_BENACHBARTE_TORE",
  E_ANTRIEB_KOLLISION: "E_ANTRIEB_KOLLISION",
  SCHIEBETOR_ANSCHLAG_KOLLISION: "SCHIEBETOR_ANSCHLAG_KOLLISION",
  DREIFACHTOR_ANSCHLAG_KOLLISION: "DREIFACHTOR_ANSCHLAG_KOLLISION",
  SCHIEBETOR_ÖFFNUNGSRICHTUNG_TORKOLLISION: "SCHIEBETOR_ÖFFNUNGSRICHTUNG_TORKOLLISION",
  PFOSTENART_UNVERAENDERLICH: "PFOSTENART_UNVERAENDERLICH",
  STECKPFOSTEN_EXTRA_LUECKE: "STECKPFOSTEN_EXTRA_LUECKE",
  BODENBEFESTIGUNG_UNVERAENDERLICH: "BODENBEFESTIGUNG_UNVERAENDERLICH",
  UMMANTELUNG_NICHT_VERFUEGBAR: "UMMANTELUNG_NICHT_VERFUEGBAR",
  LUECKE_ZWISCHEN_TEILEN: "LUECKE_ZWISCHEN_TEILEN",
  LUECKE_MAUER_ANSCHLAGPROFIL: "LUECKE_MAUER_ANSCHLAGPROFIL",
  LICHTLEISTE_WARNUNG: "LICHTLEISTE_WARNUNG",
  ECKPFOSTEN_WINKEL_UNPASSEND: "ECKPFOSTEN_WINKEL_UNPASSEND",
  TORPFOSTEN_FLACHE_SEITE: "TORPFOSTEN_FLACHE_SEITE",
  INKOMPATIBLE_NACHBARN: "INKOMPATIBLE_NACHBARN",
  TOR_MAX_BREITE: "TOR_MAX_BREITE",
  SCHIEBETOR_KOLLIDIERT_MIT_TEIL: "SCHIEBETOR_KOLLIDIERT_MIT_TEIL",
  TEIL_KOLLIDIERT_MIT_SCHIEBETOR: "TEIL_KOLLIDIERT_MIT_SCHIEBETOR",
  MAUER_KOLLIDIERT_MIT_FUNDAMENT: "MAUER_KOLLIDIERT_MIT_FUNDAMENT",
  SCHIEBETOR_DOPPELTOR_NACHBARN: "SCHIEBETOR_DOPPELTOR_NACHBARN",
  TORFLUEGEL_KOLLISION: "TORFLUEGEL_KOLLISION",
  TORFLUEGEL_KOLLISION_TEIL: "TORFLUEGEL_KOLLISION_TEIL",
  PROFILPOSITION_AUSSERHALB_BEREICH: "PROFILPOSITION_AUSSERHALB_BEREICH",
} as const;

export type WarnungDialogKey = (typeof WARNUNG_DIALOG_KEYS)[keyof typeof WARNUNG_DIALOG_KEYS];

function getHiddenWarnungDialogKeys(): Set<string> {
  try {
    const raw = sessionStorage.getItem(WARNUNG_DIALOG_HIDDEN_KEYS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function setHiddenWarnungDialogKeys(keys: Set<string>) {
  try {
    sessionStorage.setItem(
      WARNUNG_DIALOG_HIDDEN_KEYS_STORAGE_KEY,
      JSON.stringify(Array.from(keys.values()))
    );
  } catch {}
}

export function isWarnungDialogSuppressed(key: WarnungDialogKey): boolean {
  if (APP?.isTorkonfigurator) {
    const warnungDialog = APP?.richMetadata?.getWarnungDialogByKey?.(key);
    if (warnungDialog?.GesperrtMass) {
      return true;
    }
  }
  return getHiddenWarnungDialogKeys().has(key);
}

export function suppressWarnungDialog(key: WarnungDialogKey) {
  const keys = getHiddenWarnungDialogKeys();
  if (keys.has(key)) {
    return;
  }
  keys.add(key);
  setHiddenWarnungDialogKeys(keys);
}

export function showWarnungDialog(key: WarnungDialogKey) {
  const keys = getHiddenWarnungDialogKeys();
  if (!keys.delete(key)) {
    return;
  }
  setHiddenWarnungDialogKeys(keys);
}

interface ICreateWarnungDialogOptions {
  arrangement: Arrangement;
  key: WarnungDialogKey;
  text: string;
  jacos: Jaco[];
}

interface IWarnungDialogResolverContext {
  arrangement: Arrangement;
  text: string;
  jacos: Jaco[];
}

type WarnungDialogResolver = (
  context: IWarnungDialogResolverContext
) => Partial<IWarningDetailsDialog> | null;

type TorLike = Jaco & {
  id?: number;
  elementNr?: number;
  isTor?: boolean;
  isEinzeltor?: boolean;
  isSchiebetor?: boolean;
  isDoppeltor?: boolean;
  eAntriebAuswahlwert?: any;
  öffnungAuswahlwert?: any;
  anschlagAuswahlwert?: any;
  schieberichtungAuswahlwert?: any;
};

type PfostenLike = Jaco & {
  elemente?: TorLike[];
};

function getSortKey(tor: TorLike): number {
  return tor?.elementNr ?? Number.MAX_SAFE_INTEGER;
}

function getTorIdentity(tor: TorLike): number | TorLike {
  return tor?.id ?? tor;
}

function getAdjacentDreifachtore(tor: TorLike): TorLike[] {
  const segmentNachbarn = ((tor as any)?.getSegmentNachbarn?.() ?? []) as TorLike[];
  return segmentNachbarn.filter((nachbar) => nachbar?.isTor && (nachbar as any)?.isDreifachtor);
}

function getToreFromWarnung(jacos: Jaco[]): TorLike[] {
  const directTore = (jacos ?? []).filter((jaco) => (jaco as any)?.isTor) as TorLike[];
  const pfostenTore = (jacos ?? [])
    .flatMap((jaco) => ((jaco as PfostenLike)?.elemente ?? []) as TorLike[])
    .filter((tor) => tor?.isTor);

  const initialTore = _.uniqBy([...directTore, ...pfostenTore], getTorIdentity);
  const collectedTore = new Map<number | TorLike, TorLike>();
  const tore = [...initialTore];

  while (tore.length > 0) {
    const tor = tore.shift();
    if (!tor) {
      continue;
    }
    const torIdentity = getTorIdentity(tor);
    if (collectedTore.has(torIdentity)) {
      continue;
    }
    collectedTore.set(torIdentity, tor);

    const partner = getDreifachtorPartnerElement(tor as ZElement) as TorLike;
    const neighbors = _.uniqBy(
      [partner, ...getAdjacentDreifachtore(tor)].filter((nachbar) => nachbar?.isTor),
      getTorIdentity
    );
    neighbors.forEach((nachbar) => {
      if (!collectedTore.has(getTorIdentity(nachbar))) {
        tore.push(nachbar);
      }
    });
  }

  return [...collectedTore.values()].toSorted((a, b) => getSortKey(a) - getSortKey(b));
}

function getToreSortedBySceneIndex(jacos: Jaco[]): TorLike[] {
  return getToreFromWarnung(jacos).filter((tor) => tor != null);
}

function getLeftAndRightTor(jacos: Jaco[]): [TorLike | null, TorLike | null] {
  const tore = getToreSortedBySceneIndex(jacos);
  if (tore.length === 0) {
    return [null, null];
  }
  return [tore[0], tore[tore.length - 1]];
}

function applyArrangementChange(arrangement: Arrangement, callback: () => void) {
  callback();
  APP.dirty();
}

async function removeJaco(arrangement: Arrangement, jaco: Jaco | null | undefined) {
  if (!jaco) {
    return;
  }
  if ((jaco as any).isAufbau) {
    arrangement.uiRemoveAufbauten([jaco as any]);
    APP.dirty();
    return;
  }
  await arrangement.setSelectedObjects([jaco]);
  await arrangement.removeSelectedObjects();
}

function removeLeftTor(arrangement: Arrangement, jacos: Jaco[]) {
  const [leftTor] = getLeftAndRightTor(jacos);
  removeJaco(arrangement, leftTor);
}

function removeRightTor(arrangement: Arrangement, jacos: Jaco[]) {
  const [, rightTor] = getLeftAndRightTor(jacos);
  removeJaco(arrangement, rightTor);
}

function switchAnschlag(tor: TorLike) {
  if (!tor) {
    return;
  }
  const nextKey =
    tor.anschlagAuswahlwert?.Key === "Anschlag_links" ? "Anschlag_rechts" : "Anschlag_links";
  tor.anschlagAuswahlwert = APP.richMetadata.findAuswahlwert(nextKey);
}

function switchÖffnungsrichtung(tor: TorLike) {
  if (!tor) {
    return;
  }
  const nextKey =
    tor.öffnungAuswahlwert?.Key === "Öffnung_nach_innen"
      ? "Öffnung_nach_außen"
      : "Öffnung_nach_innen";
  tor.öffnungAuswahlwert = APP.richMetadata.findAuswahlwert(nextKey);
}

function switchSchieberichtung(tor: TorLike) {
  if (!tor) {
    return;
  }
  const nextKey =
    tor.schieberichtungAuswahlwert?.Key === "Schieberichtung_links"
      ? "Schieberichtung_rechts"
      : "Schieberichtung_links";
  tor.schieberichtungAuswahlwert = APP.richMetadata.findAuswahlwert(nextKey);
}
function findEinzeltor(jacos: Jaco[]): TorLike | undefined {
  return getToreFromWarnung(jacos).find((tor) => tor?.isEinzeltor);
}

function findSchiebetor(jacos: Jaco[]): TorLike | undefined {
  return getToreFromWarnung(jacos).find((tor) => tor?.isSchiebetor);
}

function findNichtSchiebetor(jacos: Jaco[]): TorLike | undefined {
  return getToreFromWarnung(jacos).find((tor) => tor?.isTor && !tor?.isSchiebetor);
}

function findDoppeltor(jacos: Jaco[], mitEAntrieb?: boolean): TorLike | undefined {
  return getToreFromWarnung(jacos).find((tor) =>
    tor?.isDoppeltor && mitEAntrieb === undefined
      ? true
      : tor?.eAntriebAuswahlwert?.Key == (mitEAntrieb ? "E_Antrieb_mit" : "E_Antrieb_ohne")
  );
}

function hasUnusedTrafoWarningText(text: string) {
  const lower = (text ?? "").toLowerCase();
  return lower.includes("trafo") && lower.includes("nicht verwendet");
}

function resolveWarnungMeta(key: WarnungDialogKey) {
  return APP.richMetadata.getWarnungDialogByKey?.(key) ?? null;
}

export function getWarnungWichtigkeitByKey(key: WarnungDialogKey): WarnungWichtigkeit {
  const wichtigkeit = resolveWarnungMeta(key)?.Wichtigkeit;
  return wichtigkeit === "niedrig" || wichtigkeit === "mittel" || wichtigkeit === "hoch"
    ? wichtigkeit
    : WARNUNG_WICHTIGKEIT_DEFAULT;
}

function selectWarnungJacos(arrangement: Arrangement, jacos: Jaco[]) {
  const selectableJacos = (jacos ?? []).filter((jaco) => jaco != null && !(jaco as any).isWarnung);
  if (selectableJacos.length === 0) {
    return;
  }
  arrangement.setSelectedObjects(selectableJacos);
  APP.zoomCameraToPart(selectableJacos[0]);
  jacos.some((j) => (j as any)?.isTor) && APP.trigger("maybeShowTorOptionen");
  APP.dirty();
}

const WARNUNG_DIALOG_RESOLVERS: Partial<Record<WarnungDialogKey, WarnungDialogResolver>> = {
  [WARNUNG_DIALOG_KEYS.ECKPFOSTEN_ZWEI_TORE]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Tor links entfernen"),
        primary: true,
        action: () => removeLeftTor(arrangement, jacos),
      },
      {
        label: I18N("warnung::Tor rechts entfernen"),
        action: () => removeRightTor(arrangement, jacos),
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.TOR_ECKPFOSTEN_OEFFNUNG]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Öffnungsrichtung wechseln"),
        primary: true,
        action: () => {
          const tor = getToreFromWarnung(jacos)[0];
          if (!tor) {
            return;
          }
          applyArrangementChange(arrangement, () => {
            if (tor.isSchiebetor) {
              return;
            } else if (tor.öffnungAuswahlwert) {
              switchÖffnungsrichtung(tor);
            } else {
              switchAnschlag(tor);
            }
          });
        },
      },
      {
        label: I18N("warnung::Betroffene Teile auswählen"),
        action: () => {
          const tore = getToreFromWarnung(jacos);
          selectWarnungJacos(arrangement, [tore as any]);
        },
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.ZWEI_SCHIEBETORE_NEBENEINANDER]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Tor links entfernen"),
        primary: true,
        action: () => removeLeftTor(arrangement, jacos),
      },
      {
        label: I18N("warnung::Tor rechts entfernen"),
        action: () => removeRightTor(arrangement, jacos),
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.ZWEI_EINZELTORE_NEBENEINANDER]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Tor links entfernen"),
        primary: true,
        action: () => removeLeftTor(arrangement, jacos),
      },
      {
        label: I18N("warnung::Tor rechts entfernen"),
        action: () => removeRightTor(arrangement, jacos),
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.ZWEI_DOPPELTORE_NEBENEINANDER]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Tor links entfernen"),
        primary: true,
        action: () => removeLeftTor(arrangement, jacos),
      },
      {
        label: I18N("warnung::Tor rechts entfernen"),
        action: () => removeRightTor(arrangement, jacos),
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.DREI_BENACHBARTE_TORE]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Äußeres linkes Tor entfernen"),
        primary: true,
        action: () => removeLeftTor(arrangement, jacos),
      },
      {
        label: I18N("warnung::Äußeres rechtes Tor entfernen"),
        action: () => removeRightTor(arrangement, jacos),
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.E_ANTRIEB_KOLLISION]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::E-Antrieb abwählen"),
        primary: true,
        action: () => {
          const tor = findDoppeltor(jacos, true);
          if (!tor) {
            return;
          }
          const setter = () =>
            (tor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"));
          (tor as ZElement).behalteLichteweite(setter);
          APP.dirty();
        },
      },
      {
        label: I18N("warnung::Betroffene Teile auswählen"),
        action: () => {
          const tor = findDoppeltor(jacos, true);
          selectWarnungJacos(arrangement, [tor]);
        },
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.SCHIEBETOR_ANSCHLAG_KOLLISION]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Anschlag wechseln"),
        primary: true,
        action: () => {
          const tor = findNichtSchiebetor(jacos);
          if (!tor) {
            return;
          }
          applyArrangementChange(arrangement, () => switchAnschlag(tor));
        },
      },
      {
        label: I18N("warnung::Betroffene Teile auswählen"),
        action: () => {
          const tor = findNichtSchiebetor(jacos);
          selectWarnungJacos(arrangement, [tor]);
        },
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.SCHIEBETOR_ÖFFNUNGSRICHTUNG_TORKOLLISION]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Öffnungsrichtung wechseln"),
        primary: true,
        action: () => {
          const schiebetor = findSchiebetor(jacos);
          if (!schiebetor) {
            return;
          }
          applyArrangementChange(arrangement, () => switchSchieberichtung(schiebetor));
        },
      },
      {
        label: I18N("warnung::Nachbartor entfernen"),
        action: () => {
          const nachbartor = findNichtSchiebetor(jacos);
          removeJaco(arrangement, nachbartor);
        },
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.DREIFACHTOR_ANSCHLAG_KOLLISION]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Einzeltoraufhängung wechseln"),
        primary: true,
        action: () => {
          const tor = findEinzeltor(jacos);
          if (!tor) {
            return;
          }
          switchAnschlag(tor);
          APP.dirty();
        },
      },
      {
        label: I18N("warnung::Betroffene Teile auswählen"),
        action: () => {
          const tor = findEinzeltor(jacos);
          if (!tor) {
            return;
          }
          selectWarnungJacos(arrangement, [tor]);
        },
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.SCHIEBETOR_KOLLIDIERT_MIT_TEIL]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Öffnungsrichtung wechseln"),
        primary: true,
        action: () => {
          const schiebetor = findSchiebetor(jacos);
          if (!schiebetor) {
            return;
          }
          applyArrangementChange(arrangement, () => switchSchieberichtung(schiebetor));
        },
      },
      {
        label: I18N("warnung::Betroffene Teile auswählen"),
        action: () => {
          const schiebetor = findSchiebetor(jacos);
          selectWarnungJacos(arrangement, [schiebetor]);
        },
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.TEIL_KOLLIDIERT_MIT_SCHIEBETOR]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Teil entfernen"),
        primary: true,
        action: () => {
          const teil = (jacos ?? []).find((jaco) => jaco != null && !(jaco as any).isWarnung);
          removeJaco(arrangement, teil);
        },
      },
      {
        label: I18N("warnung::Betroffene Teile auswählen"),
        action: () => {
          const teil = (jacos ?? []).find((jaco) => jaco != null && !(jaco as any).isWarnung);
          selectWarnungJacos(arrangement, [teil]);
        },
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.LICHTLEISTE_WARNUNG]: ({ arrangement, text, jacos }) => {
    if (!hasUnusedTrafoWarningText(text)) {
      return null;
    }
    return {
      buttons: [
        {
          label: I18N("warnung::Trafo entfernen"),
          primary: true,
          action: () => {
            const trafo = (jacos ?? []).find(
              (jaco) => (jaco as any)?.isAufbau && (jaco as any)?.isTrafo?.()
            );
            removeJaco(arrangement, trafo);
          },
        },
        {
          label: I18N("warnung::Betroffene Teile auswählen"),
          action: () => {
            const trafo = (jacos ?? []).find(
              (jaco) => (jaco as any)?.isAufbau && (jaco as any)?.isTrafo?.()
            );
            selectWarnungJacos(arrangement, [trafo]);
          },
        },
      ],
    };
  },
  [WARNUNG_DIALOG_KEYS.SCHIEBETOR_DOPPELTOR_NACHBARN]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Doppeltor entfernen"),
        primary: true,
        action: () => {
          const doppeltor = findDoppeltor(jacos);
          removeJaco(arrangement, doppeltor);
        },
      },
      {
        label: I18N("warnung::Schiebetor entfernen"),
        action: () => {
          const schiebetor = findSchiebetor(jacos);
          removeJaco(arrangement, schiebetor);
        },
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.TORFLUEGEL_KOLLISION]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Öffnungsrichtung wechseln"),
        primary: true,
        action: () => {
          const tor = getToreFromWarnung(jacos)[0];
          if (!tor) {
            return;
          }
          applyArrangementChange(arrangement, () => {
            if (tor.isSchiebetor) {
              return;
            } else if (tor.öffnungAuswahlwert) {
              switchÖffnungsrichtung(tor);
            } else {
              switchAnschlag(tor);
            }
          });
        },
      },
      {
        label: I18N("warnung::Betroffene Teile auswählen"),
        action: () => {
          const teil = (jacos ?? []).find((jaco) => jaco != null && !(jaco as any).isWarnung);
          selectWarnungJacos(arrangement, [teil]);
        },
      },
    ],
  }),
  [WARNUNG_DIALOG_KEYS.TORFLUEGEL_KOLLISION_TEIL]: ({ arrangement, jacos }) => ({
    buttons: [
      {
        label: I18N("warnung::Teil entfernen"),
        primary: true,
        action: () => {
          const teil = (jacos ?? []).find((jaco) => jaco != null && !(jaco as any).isWarnung);
          removeJaco(arrangement, teil);
        },
      },
      {
        label: I18N("warnung::Betroffene Teile auswählen"),
        action: () => {
          const teil = (jacos ?? []).find((jaco) => jaco != null && !(jaco as any).isWarnung);
          selectWarnungJacos(arrangement, [teil]);
        },
      },
    ],
  }),
};

export function createWarnungDialogConfig({
  arrangement,
  key,
  text,
  jacos,
}: ICreateWarnungDialogOptions): IWarningDetailsDialog | null {
  const resolver = WARNUNG_DIALOG_RESOLVERS[key];
  const warnungMeta = resolveWarnungMeta(key);
  if (
    !warnungMeta ||
    (Commons.isEmpty(warnungMeta?.Titel) && Commons.isEmpty(warnungMeta?.Beschreibung))
  ) {
    return null;
  }

  const selectButton = {
    label: I18N("warnung::Betroffene Teile auswählen"),
    action: () => selectWarnungJacos(arrangement, jacos),
  };

  const baseConfig: IWarningDetailsDialog = {
    title: warnungMeta?.Titel ?? I18N("warnung::Warnung im Zaunplaner"),
    text: warnungMeta?.Beschreibung ?? text,
    imageUrl: warnungMeta?.Bild,
    imageAlt: warnungMeta?.Titel ?? I18N("Warnungsbild"),
    buttons: [selectButton],
    showHideWarningCheckbox: APP.isTorkonfigurator ? false : !!warnungMeta?.Ausblendbar,
    hideWarningCheckboxLabel: I18N("warnung::Warnung nicht mehr anzeigen"),
    onHideWarningCheckboxChange: (checked) => {
      if (!checked) {
        showWarnungDialog(key);
      } else {
        suppressWarnungDialog(key);
      }
      APP.dirty();
    },
  };

  const resolvedConfig = resolver?.({ arrangement, text, jacos });

  const config: IWarningDetailsDialog = {
    ...baseConfig,
    ...(resolvedConfig ?? {}),
    buttons: resolvedConfig?.buttons ?? baseConfig.buttons ?? [],
  };
  return config;
}
